/**
 * Ayuno calculado con las comidas (Req. 23; design.md, 8.10).
 *
 * Nada se supone: un ayuno calculado no es un dato hasta que la persona lo
 * confirma, y sin registros suficientes se dice "datos insuficientes" en
 * lugar de dar por bueno un ayuno largo. Un ayuno registrado (temporizador,
 * a mano o corregido) manda sobre el cálculo del mismo periodo (Req. 23.5).
 *
 * El ayuno se asigna al día en que termina.
 */

import type { Fast, MealEntry } from '@/db/types';
import { sumarDias, type FechaLocal } from './dates';
import { fechaLocalDe, formatearHoraDeInstante } from './time';
import { formatearNumero } from './numbers';

/** Horas de más sobre la meta que hacen preguntar si faltó registrar la cena. */
export const HORAS_PARA_DUDAR = 4;
/** Un ayuno de más de esto se avisa una sola vez, sin felicitar (Req. 23.9). */
export const HORAS_AVISO_LARGO = 24;

const MS_POR_HORA = 3_600_000;

/** Un alimento que rompe el ayuno, con su instante. */
export interface EventoDeAyuno {
  momento: string;
  fecha: FechaLocal;
}

export interface Periodo {
  inicio: string;
  fin: string;
}

/**
 * Los registros que cortan el ayuno, en orden (Req. 23.10): los que no lo
 * rompen (agua, café o té sin azúcar) no entran.
 */
export function eventosDeAyuno(entradas: readonly MealEntry[]): EventoDeAyuno[] {
  return entradas
    .filter((e) => e.rompeAyuno)
    .map((e) => ({ momento: e.momento, fecha: e.fecha }))
    .filter((e) => !Number.isNaN(Date.parse(e.momento)))
    .sort((a, b) => Date.parse(a.momento) - Date.parse(b.momento));
}

export type MotivoInsuficiente =
  /** No hay nada registrado antes del primer alimento del día. */
  | 'sin_anterior'
  /** El día anterior no tiene registros: no se puede saber cuándo empezó. */
  | 'dia_anterior_sin_registros'
  /** Ese día o el anterior están marcados como registro incompleto. */
  | 'registro_incompleto';

export const TEXTO_MOTIVO: Readonly<Record<MotivoInsuficiente, string>> = {
  sin_anterior: 'No hay un alimento registrado antes del primero de ese día.',
  dia_anterior_sin_registros: 'El día anterior no tiene registros, así que no se sabe a qué hora empezó.',
  registro_incompleto: 'Ese día o el anterior están marcados como registro incompleto.',
};

export type CalculoDeAyuno =
  /** Ese día todavía no tiene un alimento registrado: no hay ayuno que termine en él. */
  | { tipo: 'sin_ayuno' }
  | { tipo: 'datos_insuficientes'; motivo: MotivoInsuficiente }
  | { tipo: 'calculado'; periodo: Periodo };

export interface DiasIncompletos {
  /** ¿Ese día está marcado como "no registré todo lo que comí"? (Req. 18.9) */
  incompleto: (fecha: FechaLocal) => boolean;
}

/**
 * El ayuno que termina en `dia`: del último alimento anterior al primero de
 * ese día (Req. 23.2). Sin un alimento anterior, con un hueco de más de un
 * día, o si ese día o el anterior están marcados como incompletos, no se
 * calcula.
 */
export function calcularAyunoQueTermina(
  dia: FechaLocal,
  eventos: readonly EventoDeAyuno[],
  dias: DiasIncompletos,
): CalculoDeAyuno {
  const primero = eventos.find((e) => e.fecha === dia);
  if (primero === undefined) return { tipo: 'sin_ayuno' };
  const ayer = sumarDias(dia, -1);
  if (dias.incompleto(dia) || dias.incompleto(ayer)) {
    return { tipo: 'datos_insuficientes', motivo: 'registro_incompleto' };
  }
  const anteriores = eventos.filter((e) => Date.parse(e.momento) < Date.parse(primero.momento));
  const anterior = anteriores[anteriores.length - 1];
  if (anterior === undefined) return { tipo: 'datos_insuficientes', motivo: 'sin_anterior' };
  if (anterior.fecha < ayer) return { tipo: 'datos_insuficientes', motivo: 'dia_anterior_sin_registros' };
  return { tipo: 'calculado', periodo: { inicio: anterior.momento, fin: primero.momento } };
}

/** Horas que duró un periodo; negativo nunca, porque el fin es posterior. */
export function horasDe(periodo: Periodo): number {
  return Math.max(0, (Date.parse(periodo.fin) - Date.parse(periodo.inicio)) / MS_POR_HORA);
}

/** "12 h 05 min", "8 h", "45 min" */
export function textoHoras(horas: number): string {
  const minutos = Math.round(Math.max(0, horas) * 60);
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')} min`;
}

/** "de 8:10 p. m. a 8:15 a. m." */
export function textoPeriodo(periodo: Periodo): string {
  return `de ${formatearHoraDeInstante(periodo.inicio)} a ${formatearHoraDeInstante(periodo.fin)}`;
}

/** ¿El periodo de un ayuno registrado es el mismo que el calculado? */
export function mismoPeriodo(a: Periodo | null | undefined, b: Periodo | null | undefined): boolean {
  if (a == null || b == null) return a == null && b == null;
  return Date.parse(a.inicio) === Date.parse(b.inicio) && Date.parse(a.fin) === Date.parse(b.fin);
}

/**
 * El ayuno guardado que termina en ese día; los abiertos (en curso) no
 * cuentan. Si hay más de uno (por ejemplo, uno confirmado con las comidas y
 * después otro con el temporizador), vale el más reciente: es lo último que
 * la persona hizo.
 */
export function ayunoRegistradoDe<T extends Pick<Fast, 'fin' | 'creadoEn'>>(ayunos: readonly T[], dia: FechaLocal): T | null {
  const delDia = ayunos
    .filter((a) => a.fin !== null && fechaLocalDe(a.fin) === dia)
    .sort((a, b) => String(a.creadoEn).localeCompare(String(b.creadoEn)));
  return delDia[delDia.length - 1] ?? null;
}

export type EstadoAyuno =
  | { tipo: 'sin_ayuno' }
  | { tipo: 'datos_insuficientes'; motivo: MotivoInsuficiente }
  /** Calculado con las comidas y pendiente de confirmar: todavía no es un dato (Req. 23.2). */
  | { tipo: 'por_confirmar'; periodo: Periodo; horas: number; dudaDeCena: boolean }
  | { tipo: 'registrado'; ayuno: Fast; periodo: Periodo; horas: number; cumplio: boolean | null }
  /** Las comidas cambiaron después de confirmarlo: se pregunta cuál conservar (Req. 23.13). */
  | {
      tipo: 'cambio';
      ayuno: Fast;
      confirmado: Periodo;
      horasConfirmadas: number;
      nuevo: Periodo;
      horasNuevas: number;
      cumplio: boolean | null;
    };

/** El periodo de un ayuno guardado; null si sigue abierto. */
function periodoDe(ayuno: Pick<Fast, 'inicio' | 'fin'>): Periodo | null {
  return ayuno.fin === null ? null : { inicio: ayuno.inicio, fin: ayuno.fin };
}

/**
 * Qué mostrar del ayuno de un día (design.md, 8.10). `metaHoras` es la de la
 * etapa vigente o, sin etapa, la de Ajustes (Req. 23.7); sin meta, no se dice
 * si se cumplió.
 */
export function estadoDelDia(
  dia: FechaLocal,
  eventos: readonly EventoDeAyuno[],
  ayunos: readonly Fast[],
  dias: DiasIncompletos,
  metaHoras: number | null,
): EstadoAyuno {
  const registrado = ayunoRegistradoDe(ayunos, dia);
  const calculo = calcularAyunoQueTermina(dia, eventos, dias);
  if (registrado !== null) {
    const periodo = periodoDe(registrado);
    if (periodo === null) return calculo.tipo === 'calculado' ? porConfirmar(calculo.periodo, metaHoras) : calculo;
    const horas = horasDe(periodo);
    const cumplio = metaHoras === null ? null : horas >= metaHoras;
    // Solo los calculados con comidas se revisan contra el cálculo de ahora:
    // un ayuno del temporizador, a mano o corregido es lo que la persona dijo
    if (registrado.origen === 'comidas' && calculo.tipo === 'calculado' && !mismoPeriodo(registrado.calculado, calculo.periodo)) {
      return {
        tipo: 'cambio',
        ayuno: registrado,
        confirmado: periodo,
        horasConfirmadas: horas,
        nuevo: calculo.periodo,
        horasNuevas: horasDe(calculo.periodo),
        cumplio,
      };
    }
    return { tipo: 'registrado', ayuno: registrado, periodo, horas, cumplio };
  }
  return calculo.tipo === 'calculado' ? porConfirmar(calculo.periodo, metaHoras) : calculo;
}

function porConfirmar(periodo: Periodo, metaHoras: number | null): EstadoAyuno {
  const horas = horasDe(periodo);
  return {
    tipo: 'por_confirmar',
    periodo,
    horas,
    // Mucho más que la meta suele ser una cena sin registrar, no un ayuno largo
    dudaDeCena: metaHoras !== null && horas > metaHoras + HORAS_PARA_DUDAR,
  };
}

export interface AyunoEnCurso {
  inicio: string;
  horas: number;
  /** Instante en que se cumple la meta; null sin meta. */
  cumpleA: string | null;
  yaCumplio: boolean;
  /** Viene de un temporizador abierto, no de las comidas. */
  conTemporizador: boolean;
}

/**
 * La ventana de alimentación en curso (0.16.0): el rato en que se está
 * comiendo, que NO es ayuno.
 *
 * Antes la app contaba como ayuno el tiempo desde la última comida, así que
 * un minuto después del desayuno decía "1 minuto de ayuno". Son dos estados
 * distintos y ahora se dicen distinto.
 */
export interface VentanaDeAlimentacion {
  /** La primera ingesta de la ventana: desde ahí se cuenta. */
  desde: string;
  horas: number;
  /** La última ingesta hasta ahora: desde ahí empieza el ayuno al cerrarla. */
  ultimaIngesta: string;
}

/** Cierra una frase sin dejar dos puntos: "8:10 p. m." ya trae el suyo. */
const conPunto = (texto: string) => (texto.endsWith('.') ? texto : `${texto}.`);

/** El último alimento que rompe el ayuno hasta `ahora`, de hoy o de ayer. */
function ultimaIngestaHasta(ahora: Date, eventos: readonly EventoDeAyuno[]): EventoDeAyuno | null {
  const hoy = fechaLocalDe(ahora.toISOString());
  const ayer = sumarDias(hoy, -1);
  return (
    [...eventos].reverse().find((e) => e.fecha >= ayer && Date.parse(e.momento) <= ahora.getTime()) ??
    null
  );
}

/**
 * ¿Está abierta la ventana de alimentación? Lo está cuando ya se comió HOY y
 * la ventana no se ha cerrado todavía.
 *
 * - Un temporizador abierto manda: ese ayuno lo empezó ella a propósito.
 * - Si la última ingesta es de ayer, no hay ventana: se está ayunando, que es
 *   lo que pasa de madrugada antes del primer alimento del día.
 * - Volver a comer después de cerrarla la vuelve a abrir, sin tener que
 *   deshacer nada.
 */
function ventanaAbierta(
  ahora: Date,
  eventos: readonly EventoDeAyuno[],
  ayunos: readonly Fast[],
  cerradaEn: string | null,
): { desde: string; ultima: string } | null {
  if (ayunos.some((a) => a.fin === null)) return null;
  const hoy = fechaLocalDe(ahora.toISOString());
  const ultima = ultimaIngestaHasta(ahora, eventos);
  if (ultima === null || ultima.fecha !== hoy) return null;
  if (cerradaEn !== null && Date.parse(ultima.momento) <= Date.parse(cerradaEn)) return null;
  const primera = eventos.find((e) => e.fecha === hoy && Date.parse(e.momento) <= ahora.getTime());
  if (primera === undefined) return null;
  return { desde: primera.momento, ultima: ultima.momento };
}

/**
 * La ventana de alimentación en curso, o null si se está ayunando. Se cuenta
 * desde la PRIMERA ingesta del día: las comidas que siguen son de la misma
 * ventana y no la reinician (Req. 23.14).
 */
export function ventanaDeAlimentacion(
  ahora: Date,
  eventos: readonly EventoDeAyuno[],
  ayunos: readonly Fast[],
  cerradaEn: string | null,
): VentanaDeAlimentacion | null {
  const ventana = ventanaAbierta(ahora, eventos, ayunos, cerradaEn);
  if (ventana === null) return null;
  return {
    desde: ventana.desde,
    horas: Math.max(0, (ahora.getTime() - Date.parse(ventana.desde)) / MS_POR_HORA),
    ultimaIngesta: ventana.ultima,
  };
}

/** "Llevas 1 min en tu ventana de alimentación, desde las 9:46 a. m." */
export function textoVentana(ventana: VentanaDeAlimentacion): string {
  const desde = formatearHoraDeInstante(ventana.desde);
  const ultima = formatearHoraDeInstante(ventana.ultimaIngesta);
  const base = `Llevas ${textoHoras(ventana.horas)} en tu ventana de alimentación, desde las ${desde}`;
  if (ventana.ultimaIngesta === ventana.desde) return conPunto(base);
  return `${conPunto(base)} Tu última ingesta fue a las ${conPunto(ultima)}`;
}

/**
 * El ayuno en curso (Req. 23.3): desde un temporizador abierto o, con el
 * método de comidas, desde el último alimento de hoy o de ayer. Más atrás no
 * se supone: un periodo sin registros no es un ayuno.
 *
 * Devuelve null mientras la ventana de alimentación está abierta: el tiempo
 * desde la última comida, estando comiendo, no es ayuno (Req. 23.14).
 */
export function ayunoEnCurso(
  ahora: Date,
  eventos: readonly EventoDeAyuno[],
  ayunos: readonly Fast[],
  metaHoras: number | null,
  cerradaEn: string | null = null,
): AyunoEnCurso | null {
  const abierto = ayunos.find((a) => a.fin === null);
  if (ventanaAbierta(ahora, eventos, ayunos, cerradaEn) !== null) return null;
  const ultimo = ultimaIngestaHasta(ahora, eventos);
  const inicio = abierto?.inicio ?? ultimo?.momento ?? null;
  if (inicio === null) return null;
  const meta = abierto?.metaHoras ?? metaHoras;
  const horas = Math.max(0, (ahora.getTime() - Date.parse(inicio)) / MS_POR_HORA);
  const cumpleA = meta === null ? null : new Date(Date.parse(inicio) + meta * MS_POR_HORA).toISOString();
  return {
    inicio,
    horas,
    cumpleA,
    yaCumplio: meta !== null && horas >= meta,
    conTemporizador: abierto !== undefined,
  };
}



/** "Tu ayuno de 12 h se cumple a las 8:10 a. m." (Req. 23.3) */
export function textoEnCurso(curso: AyunoEnCurso, metaHoras: number | null): string {
  const desde = `Llevas ${textoHoras(curso.horas)} desde las ${formatearHoraDeInstante(curso.inicio)}`;
  if (curso.cumpleA === null || metaHoras === null) return conPunto(desde);
  const meta = `${formatearNumero(metaHoras, { decimales: 1, fijos: false })} h`;
  const cumple = formatearHoraDeInstante(curso.cumpleA);
  return curso.yaCumplio
    ? conPunto(`${desde}: tu ayuno de ${meta} se cumplió a las ${cumple}`)
    : `${conPunto(desde)} ${conPunto(`Tu ayuno de ${meta} se cumple a las ${cumple}`)}`;
}

// ---------------------------------------------------------------------------
// Historial, promedios, cumplimiento y racha (Req. 23.8, 23.12; 0.6.1)
// ---------------------------------------------------------------------------

export interface DiaDeAyuno {
  fecha: FechaLocal;
  estado: EstadoAyuno;
  /** La meta de ese día; null si ese día no tenía meta. */
  metaHoras: number | null;
}

/** Las horas de un día que cuentan: solo lo registrado o confirmado (Req. 23.12). */
export function horasQueCuentan(dia: DiaDeAyuno): number | null {
  if (dia.estado.tipo === 'registrado') return dia.estado.horas;
  // Con un cambio sin resolver cuenta el valor confirmado, que es el que la persona dijo
  if (dia.estado.tipo === 'cambio') return dia.estado.horasConfirmadas;
  return null;
}

/** ¿Ese día cumplió su meta? null si no cuenta o si no tenía meta. */
export function cumplioLaMeta(dia: DiaDeAyuno): boolean | null {
  const horas = horasQueCuentan(dia);
  if (horas === null || dia.metaHoras === null) return null;
  return horas >= dia.metaHoras;
}

export interface Promedio {
  horas: number | null;
  /** Cuántos días entraron en el promedio. */
  dias: number;
}

export interface ResumenDeAyunos {
  /** Promedio de los últimos 7 y 30 días, con solo lo registrado (Req. 23.8). */
  ultimos7: Promedio;
  ultimos30: Promedio;
  /** Días del mes en curso que cumplieron su meta, de los que tenían meta. */
  cumplidosDelMes: number;
  conMetaDelMes: number;
  /** Días seguidos cumpliendo, hasta ayer (y hoy, si ya se cumplió). */
  racha: number;
  /** Días calculados que esperan confirmación: todavía no cuentan. */
  porConfirmar: number;
}

function promedioDe(dias: readonly DiaDeAyuno[], desde: FechaLocal, hasta: FechaLocal): Promedio {
  const horas = dias.filter((d) => d.fecha >= desde && d.fecha <= hasta).flatMap((d) => {
    const h = horasQueCuentan(d);
    return h === null ? [] : [h];
  });
  if (horas.length === 0) return { horas: null, dias: 0 };
  return { horas: horas.reduce((a, b) => a + b, 0) / horas.length, dias: horas.length };
}

/**
 * La racha (design.md, 8.10): días seguidos con un ayuno registrado que llegó
 * a su meta, contando hasta ayer y también hoy si ya se cumplió. Un día con
 * meta y sin ayuno registrado la corta; un día sin meta no cuenta ni corta,
 * porque no había nada que cumplir.
 */
export function rachaDeAyunos(dias: readonly DiaDeAyuno[], hoy: FechaLocal): number {
  const porFecha = new Map(dias.map((d) => [d.fecha, d]));
  const deHoy = porFecha.get(hoy);
  let racha = 0;
  // Hoy solo cuenta si ya se cumplió; si no, la racha se mide hasta ayer
  let cursor = cumplioLaMeta(deHoy ?? { fecha: hoy, estado: { tipo: 'sin_ayuno' }, metaHoras: null }) === true ? hoy : sumarDias(hoy, -1);
  for (;;) {
    const dia = porFecha.get(cursor);
    if (dia === undefined) break;
    const cumplio = cumplioLaMeta(dia);
    if (cumplio === true) racha += 1;
    else if (dia.metaHoras !== null) break;
    cursor = sumarDias(cursor, -1);
  }
  return racha;
}

/** Lo que muestra el historial (Req. 23.8). `dias` puede venir en cualquier orden. */
export function resumenDeAyunos(dias: readonly DiaDeAyuno[], hoy: FechaLocal): ResumenDeAyunos {
  const delMes = dias.filter((d) => d.fecha.slice(0, 7) === hoy.slice(0, 7) && d.metaHoras !== null);
  return {
    ultimos7: promedioDe(dias, sumarDias(hoy, -6), hoy),
    ultimos30: promedioDe(dias, sumarDias(hoy, -29), hoy),
    cumplidosDelMes: delMes.filter((d) => cumplioLaMeta(d) === true).length,
    conMetaDelMes: delMes.length,
    racha: rachaDeAyunos(dias, hoy),
    porConfirmar: dias.filter((d) => d.estado.tipo === 'por_confirmar').length,
  };
}

/** "3 días seguidos", "1 día seguido", "Sin racha todavía" */
export function textoRacha(racha: number): string {
  if (racha === 0) return 'Sin racha todavía';
  return racha === 1 ? '1 día seguido' : `${racha} días seguidos`;
}

// ---------------------------------------------------------------------------
// Aviso de un ayuno largo (Req. 23.9; 0.6.2)
// ---------------------------------------------------------------------------

export interface AvisoDeAyunoLargo {
  /** "curso:2026-10-27" o "dia:2026-10-28": con qué se recuerda que ya se mostró. */
  clave: string;
  horas: number;
  /** El que va corriendo ahora, no uno que ya terminó. */
  enCurso: boolean;
}

/**
 * Un ayuno de más de 24 horas se avisa una sola vez (Req. 23.9), sin
 * felicitar por la duración: puede ser que falte registrar una comida. Se
 * mira primero el que va corriendo, que es el que importa ahora.
 */
export function avisoDeAyunoLargo(
  dia: FechaLocal,
  curso: AyunoEnCurso | null,
  estado: EstadoAyuno,
  visto: string | null,
): AvisoDeAyunoLargo | null {
  const candidato = (): AvisoDeAyunoLargo | null => {
    if (curso !== null && curso.horas >= HORAS_AVISO_LARGO) {
      return { clave: `curso:${fechaLocalDe(curso.inicio)}`, horas: curso.horas, enCurso: true };
    }
    const horas =
      estado.tipo === 'registrado' || estado.tipo === 'por_confirmar'
        ? estado.horas
        : estado.tipo === 'cambio'
          ? estado.horasConfirmadas
          : null;
    if (horas !== null && horas >= HORAS_AVISO_LARGO) return { clave: `dia:${dia}`, horas, enCurso: false };
    return null;
  };
  const aviso = candidato();
  return aviso === null || aviso.clave === visto ? null : aviso;
}

/** Lo que dice el aviso: sin felicitar y recordando que pudo faltar un registro. */
export function textoAvisoLargo(aviso: AvisoDeAyunoLargo): string {
  const cuanto = textoHoras(aviso.horas);
  return aviso.enCurso
    ? `Llevas ${cuanto} sin registrar alimento. Si está siendo así, cuida cómo te sientes; si faltó registrar algo, puedes anotarlo.`
    : `Ese ayuno quedó en ${cuanto}. Si fue así, cuida cómo te sientes; si faltó registrar una comida, puedes corregir sus horas.`;
}
